'use client'

import { useCallback, useEffect, useState } from 'react'

import {
  useConversationContext,
  useDropOffPointMapContext,
} from '@marketplace-web/domain/core-conversation'
import { ConversationLinkActions } from '@marketplace-web/domain/conversation-messaging-data'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/shared/event-tracker'

import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { getDefaultShippingAddress } from 'data/api/get-default-shipping-address'
import { transformDefaultShippingAddress } from 'data/api/transformers/response/transform-default-shipping-address'

import DropOffPointModal from '../DropOffPointModal'

const DropOffPointMapLink = () => {
  const { track } = useTracking()
  const { fetchNearbyDropOffPoints } = useDropOffPointMapContext()
  const { conversation } = useConversationContext()

  const transactionId = conversation?.transaction?.id

  const [isModalOpen, setIsModalOpen] = useState(false)

  const { fetch: fetchDefaultShippingAddress, transformedData: defaultShippingAddress } = useFetch(
    getDefaultShippingAddress,
    transformDefaultShippingAddress,
  )

  useEffect(() => {
    if (!isModalOpen || !defaultShippingAddress) return

    fetchNearbyDropOffPoints({ address: defaultShippingAddress })
  }, [defaultShippingAddress, fetchNearbyDropOffPoints, isModalOpen])

  useEffect(() => {
    if (defaultShippingAddress) return

    if (isModalOpen) fetchDefaultShippingAddress()
  }, [defaultShippingAddress, fetchDefaultShippingAddress, isModalOpen])

  const trackLinkClick = useCallback(() => {
    if (!transactionId) return

    const targetDetails = JSON.stringify({
      transaction_id: transactionId,
    })

    track(
      clickEvent({
        target: ClickableElement.DropOffPointsMapLink,
        targetDetails,
      }),
    )
  }, [track, transactionId])

  const handleClose = () => setIsModalOpen(false)

  const handleModalOpen = useCallback(() => {
    trackLinkClick()
    setIsModalOpen(true)
  }, [trackLinkClick, setIsModalOpen])

  useEffect(() => {
    const handleShipmentLinkClick = event => {
      const element = event.target as HTMLElement

      if (!element.classList.contains(ConversationLinkActions.DropOffPointMapLink)) return

      event.preventDefault()
      handleModalOpen()
    }

    document.body.addEventListener('click', handleShipmentLinkClick)

    return () => {
      document.body.removeEventListener('click', handleShipmentLinkClick)
    }
  }, [handleModalOpen])

  return <DropOffPointModal isOpen={isModalOpen} onClose={handleClose} />
}

export default DropOffPointMapLink
